"use client";

import { useEffect, useState, type CSSProperties } from "react";

import { useCcLatchInView } from "@/lib/use-cc-latch-in-view";

const CHART_RADIUS = 49;
const CHART_CIRCUMFERENCE = 2 * Math.PI * CHART_RADIUS;
const CHART_PROGRESS = 0.8;
const CHART_OFFSET = CHART_CIRCUMFERENCE * (1 - CHART_PROGRESS);
const CHART_PERCENT = Math.round(CHART_PROGRESS * 100);

const DONUT_ANIM_DURATION_MS = 2000;

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

export type CampaignAutomationStatCopy = {
  label: string;
  value: string;
  icon: "crawl" | "index" | "cluster";
};

export type CampaignAutomationDemoCopy = {
  chartLabel: string;
  chartAmount: string;
  stats: CampaignAutomationStatCopy[];
};

function CampaignStatIcon({ type }: { type: CampaignAutomationStatCopy["icon"] }) {
  if (type === "crawl") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle
          cx="12"
          cy="12"
          r="8.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M2 12h4M18 12h4M12 2v4M12 18v4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M12 8v4l2.5 2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "index") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M7 4h10a1.5 1.5 0 011.5 1.5V18a1.5 1.5 0 01-1.5 1.5H7A1.5 1.5 0 015.5 18V5.5A1.5 1.5 0 017 4z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M9 9h6M9 12.5h4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M15.5 16.5l1.5 1.5 3-3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 7h14M5 12h10M5 17h6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M16 15l3 3-3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 18h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

type CcCampaignAutomationDemoProps = {
  copy: CampaignAutomationDemoCopy;
};

export function CcCampaignAutomationDemo({ copy }: CcCampaignAutomationDemoProps) {
  const { ref: donutRef, isInView: isDonutVisible } = useCcLatchInView<HTMLDivElement>({
    threshold: 0.3,
  });
  const [displayPercent, setDisplayPercent] = useState(0);

  useEffect(() => {
    if (!isDonutVisible) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      setDisplayPercent(CHART_PERCENT);
      return;
    }

    let startTime: number | null = null;
    let frame = 0;

    const tick = (now: number) => {
      if (startTime === null) {
        startTime = now;
      }

      const progress = Math.min(1, (now - startTime) / DONUT_ANIM_DURATION_MS);
      setDisplayPercent(Math.round(easeInOut(progress) * CHART_PERCENT));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [isDonutVisible]);

  return (
    <div className="cc-growth-features__campaign">
      <div className="cc-growth-features__campaign-surface">
        <div className="cc-growth-features__campaign-inner-surface">
          <div
            className={[
              "cc-growth-features__campaign-layout",
              isDonutVisible && "cc-growth-features__campaign-layout--visible",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="cc-growth-features__campaign-chart">
              <span className="cc-growth-features__campaign-chart-label">
                {copy.chartLabel}
              </span>
              <div
                ref={donutRef}
                className={[
                  "cc-growth-features__campaign-donut",
                  isDonutVisible && "cc-growth-features__campaign-donut--visible",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={
                  {
                    "--campaign-donut-circumference": CHART_CIRCUMFERENCE,
                    "--campaign-donut-offset": CHART_OFFSET,
                  } as CSSProperties
                }
              >
                <svg
                  className="cc-growth-features__campaign-donut-svg"
                  viewBox="0 0 110 110"
                  role="img"
                  aria-label={`${copy.chartLabel} ${CHART_PERCENT} percent`}
                >
                  <circle
                    className="cc-growth-features__campaign-donut-track"
                    cx="55"
                    cy="55"
                    r={CHART_RADIUS}
                  />
                  <circle
                    className="cc-growth-features__campaign-donut-progress"
                    cx="55"
                    cy="55"
                    r={CHART_RADIUS}
                  />
                </svg>
                <span className="cc-growth-features__campaign-donut-value">
                  {displayPercent}%
                </span>
              </div>
              <span className="cc-growth-features__campaign-chart-amount">
                {copy.chartAmount}
              </span>
            </div>

            <ul className="cc-growth-features__campaign-stats">
              {copy.stats.map((stat, index) => (
                <li
                  key={stat.label}
                  className="cc-growth-features__campaign-stat"
                  style={{ "--campaign-stat-index": index } as CSSProperties}
                >
                  <div className="cc-growth-features__campaign-stat-copy">
                    <span className="cc-growth-features__campaign-stat-label">
                      {stat.label}
                    </span>
                    <span className="cc-growth-features__campaign-stat-value">
                      {stat.value}
                    </span>
                  </div>
                  <span className="cc-growth-features__campaign-stat-icon">
                    <CampaignStatIcon type={stat.icon} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
